import { type CSSProperties, type KeyboardEvent, type MouseEvent, type ReactNode, useRef } from 'react';
import {
  clampColumnWidth,
  COLUMN_WIDTH_FALLBACK_PX,
  COLUMN_WIDTH_KEYBOARD_STEP_PX,
  COLUMN_WIDTH_MAX_PX,
  COLUMN_WIDTH_MIN_PX,
  type ColumnWidths,
} from './useColumnWidths';
import { RowActionsMenu, type RowActionsMenuItem } from './RowActionsMenu';

export type SortDir = 'asc' | 'desc';

export interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => ReactNode;
  className?: string;
  /** When true, the desktop header becomes an interactive tri-state sort control
   *  (none → asc → desc → none). Requires `sortBy`/`sortDir`/`onSort` on the Table
   *  (#13136). Ignored on the mobile card layout, which has no header row. */
  sortable?: boolean;
  /** Text alignment for both the header and the cell (#13185 — "columns don't line
   *  up" mockup feedback). `'right'` also applies `tabular-nums` to the cell so
   *  numeric/date figures of varying digit-width line up vertically across rows —
   *  the concrete anti-jitter mechanism named in the mockup. Defaults to `'left'`.
   *  Desktop-table only — the mobile card layout (label above value) has no
   *  alignment concept and is unaffected. */
  align?: 'left' | 'center' | 'right';
  /** Phone compact-card priority (BR-MOB-005, #22193). Only read when the Table opts into
   *  `compactCards`: `primary` is the card title, `status` its badge, at most two `secondary`
   *  columns sit below, and `hidden` (or any undeclared column, once ANY column declares a
   *  priority) is never rendered on the card. Desktop rendering ignores it. */
  mobile?: ColumnMobilePriority;
  /** Plain, non-interactive card content for this column (BR-MOB-005). The whole compact card is
   *  one control, so a column whose desktop `render` holds a link or button must supply this, or
   *  the card would nest interactive content. Falls back to `render`, then the raw value. */
  mobileRender?: (item: T) => ReactNode;
}

/** BR-MOB-020 (#22206): one row's actions on a phone compact card. `name` completes the
 *  trigger's accessible name ("Actions for <name>"); `items` are the row's existing actions with
 *  their existing handlers and enable/disable rules. Return `null` for a row with no actions. */
export interface CardRowActions {
  name: string;
  items: RowActionsMenuItem[];
  /** Disables the whole trigger (prefer disabling individual items so they still announce). */
  disabled?: boolean;
}

/** BR-MOB-005: where a column sits on a phone compact card. */
export type ColumnMobilePriority = 'primary' | 'status' | 'secondary' | 'hidden';

/** The trailing row-actions cells in this codebase carry a blank header or the `actions` key;
 *  they are never shown on a phone card (operator decision 2026-10-02). */
const isActionColumn = (col: { key: string; header: string }): boolean =>
  col.header.trim() === '' || col.key === 'actions';

/** Resolves which columns a compact card shows (BR-MOB-005). With no declared priority: the
 *  first column is the title, the next two non-action columns are secondary, the rest hidden. */
export function compactCardColumns<T>(columns: Column<T>[]): {
  title?: Column<T>;
  status?: Column<T>;
  secondary: Column<T>[];
} {
  const eligible = columns.filter(c => !isActionColumn(c));
  const declared = eligible.some(c => c.mobile !== undefined);
  if (!declared) {
    const [title, ...rest] = eligible;
    return { title, secondary: rest.slice(0, 2) };
  }
  return {
    title: eligible.find(c => c.mobile === 'primary'),
    status: eligible.find(c => c.mobile === 'status'),
    secondary: eligible.filter(c => c.mobile === 'secondary').slice(0, 2),
  };
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  /** When true (default), renders a stacked card layout below the md breakpoint (<768px)
   *  in addition to the standard desktop table. Set to false to suppress the card
   *  markup entirely and render only the desktop table at all viewports. */
  responsive?: boolean;
  /** Column `key` currently sorted, when the Table is externally sort-controlled (#13136). */
  sortBy?: string;
  sortDir?: SortDir;
  /** Fires when a sortable header is clicked. Cycles: unsorted → asc → desc → unsorted
   *  (`dir` is `undefined` on the third click — the caller clears the sort). The Table
   *  holds no sort state itself; it reflects `sortBy`/`sortDir` and reports the next
   *  state on click, leaving the caller (e.g. `useListQuery`) to own the truth. */
  onSort?: (key: string, dir: SortDir | undefined) => void;
  /** Optional per-row className (e.g. overdue-row emphasis) applied to the desktop <tr> and the
   *  mobile card wrapper, in addition to the row-click hover styles. #account-invoice-columns. */
  rowClassName?: (item: T) => string;
  /** Compact body text — data cells render at text-xs instead of text-sm (operator 2026-07-14).
   *  Headers are already text-xs; this only affects the row cells + the mobile-card values. */
  dense?: boolean;
  /** Operator-set pixel widths keyed by column `key` (#13249). A key that is absent keeps its
   *  natural width. An EMPTY map (or the prop omitted) renders exactly as the Table always has
   *  — that is the AC5 no-regression contract for the ~20 lists on the shared shell. */
  columnWidths?: ColumnWidths;
  /** Supplied → every named desktop header gains a pointer + keyboard resize control (#13249).
   *  Receives the FULL next width map; the Table holds no width state itself, exactly as it
   *  holds no sort state (see `onSort`). Omit to render a non-resizable table. */
  onColumnWidthsChange?: (widths: ColumnWidths) => void;
  /** Opt-in phone compact cards (BR-MOB-005, #22193). Default `false`: below md the existing
   *  stacked label/value card renders unchanged, which the client portal relies on. When `true`,
   *  each card shows the title + status badge, at most two key fields and a chevron. */
  compactCards?: boolean;
  /** What tapping a compact card opens. Defaults to `onRowClick`. Lets a list whose desktop
   *  rows are NOT clickable (their cells carry their own buttons) still open the record from the
   *  card, without making the desktop row clickable. Ignored unless `compactCards`. */
  onCardClick?: (item: T) => void;
  /** BR-MOB-020 (#22206): the row's actions on a phone. When given, each compact card whose row
   *  returns actions renders exactly ONE 44px "Actions for <name>" button that opens the shared
   *  `RowActionsMenu` — the only way to reach row actions on a phone (no swipe, no long-press).
   *  Ignored unless `compactCards`; the desktop table and its actions column are untouched. */
  rowActions?: (item: T) => CardRowActions | null | undefined;
  /** BR-MOB-021 (#22206): set `false` on a list with no detail page. The card body then does
   *  nothing on tap and carries no chevron, even if `onRowClick` is set for the desktop rows.
   *  Defaults to `true`. Ignored unless `compactCards`. */
  hasDetailPage?: boolean;
}

export function Table<T extends object>({
  columns, data, keyField, loading, emptyMessage = 'No records found', onRowClick, responsive = true,
  sortBy, sortDir, onSort, rowClassName, dense, columnWidths, onColumnWidthsChange,
  compactCards = false, onCardClick, rowActions, hasDetailPage = true,
}: TableProps<T>) {
  const tableRef = useRef<HTMLTableElement>(null);
  const handleSortClick = (key: string) => {
    if (!onSort) return;
    if (sortBy !== key) {
      onSort(key, 'asc');
    } else if (sortDir === 'asc') {
      onSort(key, 'desc');
    } else {
      onSort(key, undefined);
    }
  };

  const sortAriaLabel = (col: Column<T>): string => {
    if (sortBy !== col.key) return `Sort by ${col.header} ascending`;
    return sortDir === 'asc' ? `Sort by ${col.header} descending` : `Clear sort by ${col.header}`;
  };

  /** a11y (#14639): exposes the column's current sort state to assistive tech.
   *  The `aria-label` already announces the *next* action ("Sort by Name descending");
   *  without `aria-sort` a screen-reader user could never perceive the *current* state,
   *  so the tri-state control (#13136) was only half-observable. Only sortable columns
   *  carry the attribute — on a non-sortable column `aria-sort` is meaningless, and
   *  'none' there would wrongly imply it could be sorted. */
  const ariaSort = (col: Column<T>): 'ascending' | 'descending' | 'none' | undefined => {
    if (!col.sortable || !onSort) return undefined;
    if (sortBy !== col.key) return 'none';
    return sortDir === 'desc' ? 'descending' : 'ascending';
  };

  const sortIndicator = (key: string): ReactNode => {
    if (sortBy !== key) return <span aria-hidden="true" className="text-gray-300">⇅</span>;
    return <span aria-hidden="true">{sortDir === 'desc' ? '▼' : '▲'}</span>;
  };

  // a11y (#14639): this control previously carried `focus:outline-none` with no
  // replacement, so keyboard-driven sorting was invisible — a WCAG 2.1 AA 2.4.7
  // (Focus Visible) failure on every sortable list, i.e. on the screens that
  // correctly use the shared primitive. Now uses the design system's focus-visible
  // ring (same tokens as Button.tsx), so the indicator appears for keyboard users
  // without adding a ring on mouse click. `rounded` gives the offset ring a sane
  // shape around the inline-flex control.
  const SORT_BUTTON_CLASS =
    'flex items-center gap-1 rounded hover:text-gray-700 '
    + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';

  const renderHeaderCell = (col: Column<T>): ReactNode => {
    if (!col.sortable || !onSort) return col.header;
    return (
      <button
        type="button"
        onClick={() => handleSortClick(col.key)}
        aria-label={sortAriaLabel(col)}
        className={SORT_BUTTON_CLASS}
      >
        {col.header}
        {sortIndicator(col.key)}
      </button>
    );
  };


  // ---------------------------------------------------------------------------
  // Resizable column widths (#13249) — shared by every list on DataListShell.
  // ---------------------------------------------------------------------------

  /** Resizing is opt-in per Table: no callback → no handles, no width styles, no layout
   *  change. `DataListShell` opts every list in; a Table used directly is untouched. */
  const resizable = Boolean(onColumnWidthsChange);

  /** A column with an empty header gets no handle — there is nothing to name it with (an
   *  `aria-label` of "Resize  column" is worse than no control), and the columns that carry
   *  a blank header in this codebase are the trailing row-actions cells, which nobody sizes. */
  const canResize = (col: Column<T>): boolean => resizable && col.header !== '';

  /** Measure what every column is CURRENTLY occupying, so the first drag can pin the other
   *  columns at the size they already have. Without this, switching the table to `table-fixed`
   *  (below) would let the browser redistribute every unsized column the moment one width is
   *  set — a visible jump on all ~20 consumers. jsdom has no layout engine and reports 0, so
   *  zero-width measurements are discarded rather than stored. */
  const measuredWidths = (): ColumnWidths => {
    const out: ColumnWidths = {};
    const headerCells = tableRef.current?.querySelectorAll('thead th');
    headerCells?.forEach((cell, index) => {
      const col = columns[index];
      const width = (cell as HTMLElement).getBoundingClientRect().width;
      if (col && width > 0) out[col.key] = clampColumnWidth(width);
    });
    return out;
  };

  /** Stored widths always win over measurements. */
  const widthBaseline = (): ColumnWidths => ({ ...measuredWidths(), ...(columnWidths ?? {}) });

  const commitWidth = (baseline: ColumnWidths, key: string, width: number) => {
    onColumnWidthsChange?.({ ...baseline, [key]: clampColumnWidth(width) });
  };

  const beginPointerResize = (event: MouseEvent<HTMLDivElement>, key: string) => {
    if (!onColumnWidthsChange) return;
    // preventDefault stops the drag turning into a text selection; stopPropagation keeps the
    // gesture out of the row-click handler and the sort button beside it.
    event.preventDefault();
    event.stopPropagation();
    const baseline = widthBaseline();
    const startX = event.clientX;
    const startWidth = baseline[key] ?? COLUMN_WIDTH_FALLBACK_PX;

    const onMove = (moveEvent: globalThis.MouseEvent) => {
      commitWidth(baseline, key, startWidth + (moveEvent.clientX - startX));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const resetColumn = (key: string) => {
    if (!onColumnWidthsChange) return;
    const next = { ...(columnWidths ?? {}) };
    delete next[key];
    onColumnWidthsChange(next);
  };

  /** AC6 — the resize control is genuinely keyboard-operable, not a documented pointer-only
   *  punt: it is a focusable `separator` (the WAI-ARIA window-splitter pattern), Arrow keys
   *  step the width, and Home returns that one column to its natural width. */
  const handleResizeKey = (event: KeyboardEvent<HTMLDivElement>, key: string) => {
    if (!onColumnWidthsChange) return;
    const baseline = widthBaseline();
    const current = baseline[key] ?? COLUMN_WIDTH_FALLBACK_PX;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      commitWidth(baseline, key, current + COLUMN_WIDTH_KEYBOARD_STEP_PX);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      commitWidth(baseline, key, current - COLUMN_WIDTH_KEYBOARD_STEP_PX);
    } else if (event.key === 'Home') {
      event.preventDefault();
      resetColumn(key);
    }
  };

  /** Only switch to fixed layout once a width actually exists. With no stored widths the
   *  table keeps `min-w-full` auto layout — the AC5 "renders exactly as it does today" path. */
  const hasStoredWidths = Object.keys(columnWidths ?? {}).length > 0;

  const widthStyle = (key: string): CSSProperties | undefined => {
    const width = columnWidths?.[key];
    // min+max as well as width: under auto layout `width` alone is only a hint, and a cell
    // with `whitespace-nowrap` content would quietly refuse to shrink to the operator's size.
    return width ? { width, minWidth: width, maxWidth: width } : undefined;
  };

  const RESIZE_HANDLE_CLASS =
    'absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none touch-none '
    + 'hover:bg-indigo-300 focus-visible:outline-none focus-visible:bg-indigo-400 '
    + 'focus-visible:ring-2 focus-visible:ring-indigo-500';

  const renderResizeHandle = (col: Column<T>): ReactNode => {
    if (!canResize(col)) return null;
    return (
      /* A FOCUSABLE separator is precisely the WAI-ARIA "Window Splitter" pattern
         (role=separator + tabindex=0 + aria-value*), which is what makes AC6's keyboard
         resize possible at all. jsx-a11y classifies `separator` as non-interactive and so
         objects to both the tabIndex and the handlers; the alternative — widening the rule's
         `tabbable` option repo-wide — would relax the check for every component, so the
         exception is scoped to the one element that needs it. */
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <div
        role="separator"
        aria-orientation="vertical"
        /* NOT aria-label. A header cell takes its accessible name from its CONTENT, and a
           labelled descendant folds into that name — an aria-label here renames every header
           from "Company" to "Company Resize Company column" and breaks the exact-name
           `getByRole('columnheader', { name })` assertions across the consumer suites. Naming
           the <th> instead fixes the name but makes the header answer `getByLabelText`, which
           then collides with same-named form fields on consumer pages (CompanyManagement's
           "ABN" / "Legal name" — 8 broken tests). `aria-roledescription` is the way out: it is
           announced in place of the bare "separator" role, but contributes nothing to any name
           computation, so the header stays exactly as named as it was. Together with the
           orientation + value attributes a screen reader announces "column width, 220, 60 to
           900" — and the control is genuinely keyboard-operable (AC6), not pointer-only. */
        aria-roledescription="Column width"
        aria-valuenow={columnWidths?.[col.key] ?? COLUMN_WIDTH_FALLBACK_PX}
        aria-valuemin={COLUMN_WIDTH_MIN_PX}
        aria-valuemax={COLUMN_WIDTH_MAX_PX}
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
        tabIndex={0}
        data-testid={`column-resize-${col.key}`}
        className={RESIZE_HANDLE_CLASS}
        onMouseDown={event => beginPointerResize(event, col.key)}
        onKeyDown={event => handleResizeKey(event, col.key)}
        onDoubleClick={() => resetColumn(col.key)}
        onClick={event => event.stopPropagation()}
      />
    );
  };

  if (loading) {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map(col => (
                <th key={col.key} scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                {columns.map(col => (
                  <td key={col.key} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded animate-pulse" /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  const desktopTable = (
    <div className={responsive ? 'hidden md:block overflow-x-auto' : 'overflow-x-auto'}>
      <table ref={tableRef} className={`min-w-full divide-y divide-gray-200${hasStoredWidths ? ' table-fixed' : ''}`}>
        {/* z-10 (#13185): keeps the sticky header above scrolling row content —
            without it, hovered/selected row backgrounds can render on top of the
            pinned header at the scroll boundary. */}
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            {/* NOTE (#13249): the header cell is deliberately left UNNAMED. A name-from-content
                cell takes its name from descendant TEXT, not from a descendant's aria-label —
                which the pre-existing sort button already proves (it carries
                aria-label="Sort by Name ascending" while the cell still resolves to "Name").
                The resize handle below has no text content, so it contributes nothing and every
                `getByRole('columnheader', { name })` assertion keeps working. Naming the cell
                explicitly here is actively WRONG: an aria-label on the <th> makes the header
                answer `getByLabelText`, which collides with the form fields of the same name on
                pages like CompanyManagement ("ABN", "Legal name") and breaks their tests. */}
            {columns.map(col => (
              <th
                key={col.key}
                scope="col"
                aria-sort={ariaSort(col)}
                style={widthStyle(col.key)}
                className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide ${canResize(col) ? 'relative ' : ''}${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'} ${col.className || ''}`}
              >
                {renderHeaderCell(col)}
                {renderResizeHandle(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map(item => (
            <tr
              key={String(item[keyField])}
              className={`${onRowClick ? 'hover:bg-gray-50 cursor-pointer' : ''} ${rowClassName?.(item) ?? ''}`.trim()}
              onClick={() => onRowClick?.(item)}
            >
              {columns.map(col => (
                <td
                  key={col.key}
                  style={widthStyle(col.key)}
                  className={`px-4 py-3 ${dense ? 'text-xs' : 'text-sm'} text-gray-700 ${col.align === 'right' ? 'text-right tabular-nums' : col.align === 'center' ? 'text-center tabular-nums' : ''} ${col.className || ''}`}
                >
                  {col.render ? col.render(item) : String((item as Record<string, unknown>)[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ---------------------------------------------------------------------------
  // BR-MOB-005 (#22193): opt-in phone compact cards. Title + status badge on the first line,
  // at most two key fields below, a chevron; the whole card is ONE control that opens the record.
  // Cell content comes from `mobileRender` (plain) so no link or button nests inside the card.
  // ---------------------------------------------------------------------------
  const cardContent = (col: Column<T>, item: T): ReactNode => {
    if (col.mobileRender) return col.mobileRender(item);
    if (col.render) return col.render(item);
    return String((item as Record<string, unknown>)[col.key] ?? '');
  };

  const renderCompactCards = (): ReactNode => {
    const { title, status, secondary } = compactCardColumns(columns);
    // BR-MOB-021: a list with no detail page has nothing to open, whatever its desktop rows do.
    const open = hasDetailPage ? (onCardClick ?? onRowClick) : undefined;
    const CARD_CLASS = 'relative block w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm';
    // BR-MOB-020: the 44x44px menu trigger. The shared RowActionsMenu trigger is 28px (w-7 h-7)
    // for the dense desktop actions column; on the card its root's trigger is enlarged from here
    // so the component itself stays untouched (the table's own actions column is unchanged).
    const MENU_SLOT_CLASS =
      'absolute inset-y-0 right-1 flex items-center [&>div>button]:h-11 [&>div>button]:w-11 [&>div>button]:text-base';
    return (
      <ul className="block md:hidden space-y-2" data-testid="table-cards">
        {data.map(item => {
          const body = (
            <>
              <span className="flex items-start justify-between gap-2">
                <span data-m="title" className="min-w-0 truncate text-sm font-semibold text-gray-900">
                  {title ? cardContent(title, item) : null}
                </span>
                {status && (
                  <span data-m="status" className="shrink-0">{cardContent(status, item)}</span>
                )}
              </span>
              {secondary.map(col => (
                <span key={col.key} data-m="key" className="mt-0.5 block truncate text-xs text-gray-500">
                  {cardContent(col, item)}
                </span>
              ))}
            </>
          );
          const extra = rowClassName?.(item) ?? '';
          const actions = rowActions?.(item) ?? null;
          // The trigger is a SIBLING of the card control, never inside it: a button nested in the
          // card button would be axe nested-interactive, and a tap on it must never open the record.
          const menu = actions && (
            <div data-testid="card-actions" className={MENU_SLOT_CLASS}>
              <RowActionsMenu label={`Actions for ${actions.name}`} items={actions.items} disabled={actions.disabled} />
            </div>
          );
          return (
            <li key={String(item[keyField])} className={menu ? 'relative' : undefined}>
              {open ? (
                <button
                  type="button"
                  data-testid="compact-card"
                  onClick={() => open(item)}
                  className={`${CARD_CLASS} ${menu ? 'pr-20' : 'pr-9'} cursor-pointer hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${extra}`.trim()}
                >
                  {body}
                  <svg
                    data-testid="card-chevron"
                    aria-hidden="true"
                    className={`absolute ${menu ? 'right-14' : 'right-3'} top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <div data-testid="compact-card" className={`${CARD_CLASS} ${menu ? 'pr-14 ' : ''}${extra}`.trim()}>
                  {body}
                </div>
              )}
              {menu}
            </li>
          );
        })}
      </ul>
    );
  };

  if (!responsive) {
    return desktopTable;
  }

  return (
    <>
      {desktopTable}
      {compactCards ? renderCompactCards() : (
        <>
          {/* Mobile card layout: visible below md breakpoint, hidden at md and above.
              a11y (#11926): the card is PRESENTATIONAL — NOT a clickable button. A
              role="button"/onClick wrapper nests interactive cell content (links, buttons) →
              axe nested-interactive (serious WCAG, seen on MyCases), and a bare onClick <div>
              trips jsx-a11y. Mobile navigation is via the row's own interactive cells (e.g. the
              case-number / subject links). The desktop <tr> above remains row-clickable. */}
          <div className="block md:hidden space-y-3" data-testid="table-cards">
            {data.map(item => (
              <div
                key={String(item[keyField])}
                className={`bg-white rounded-lg border border-gray-200 p-4 shadow-sm ${rowClassName?.(item) ?? ''}`.trim()}
              >
                {columns.map(col => (
                  <div key={col.key} className={`flex flex-col py-1${col.className ? ` ${col.className}` : ''}`} data-label={col.header}>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{col.header}</span>
                    <span className={`${dense ? 'text-xs' : 'text-sm'} text-gray-700`}>
                      {col.render ? col.render(item) : String((item as Record<string, unknown>)[col.key] ?? '')}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
